import React from 'react'
import moment from 'moment'
import styled from 'styled-components/native'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'

import Typography from 'APP/Converse/Typography'
import I18n from 'APP/I18n'
import { Images } from 'APP/Themes'
import { ViewProps } from 'react-native'
import { useTheme } from 'styled-components/native'

type Props = {
  date?: string | null
  timeslot?: string | null
  providerName?: string | null
  providerRole?: string | null
  icon?: string
  issueTypeId?: string | null
  reasonForConsult?: string | null
} & ViewProps

const Container = styled.View`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: ${({ theme }) => theme.metrics.baseMargin / 2}px 0;
  border-radius: ${({ theme }) => theme.metrics.borderRadius.medium}px;
  background-color: ${({ theme }) => theme.colors.elementsBg};
  ${({ theme }) => theme.shadows.soft};
`

const DateContainer = styled.View`
  justify-content: center;
  align-items: center;
  align-self: stretch;
  background-color: ${({ theme }) => theme.colors.appointmentDateElement};
  border-top-left-radius: ${({ theme }) => theme.metrics.baseMargin}px;
  border-bottom-left-radius: ${({ theme }) => theme.metrics.baseMargin}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const AppointmentDetails = styled.View`
  flex: 1;
  margin: ${({ theme }) => theme.metrics.baseMargin}px;
`

const TimeContainer = styled.View`
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin / 1.5}px;
`

const ScheduleIcon = styled(MaterialIcon)`
  margin-right: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  color: ${({ theme }) => theme.colors.accent};
`

const TimeCopy = styled(Typography)`
  flex: 1;
  flex-wrap: wrap;
`

const ProviderContainer = styled.View`
  flex-direction: row;
  align-items: center;
`

const ProviderImage = styled.Image`
  height: ${({ theme }) => theme.metrics.icon.large};
  width: ${({ theme }) => theme.metrics.icon.large};
  margin-right: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  border-radius: ${({ theme }) => theme.metrics.icon.large / 2};
`

const ProviderInnerContainer = styled.View`
  flex: 1;
  align-self: center;
  gap: ${({ theme }) => theme.metrics.baseMargin / 4}px;
`

const ReasonContainer = styled.View`
  border-top-color: ${({ theme }) => theme.colors.elementsBorder};
  border-top-width: 1px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-top: ${({ theme }) => theme.metrics.baseMargin}px;
`

const AppointmentCardStatic = ({
  date,
  timeslot,
  icon,
  providerName,
  providerRole,
  issueTypeId,
  reasonForConsult,
  ...rest
}: Props) => {
  const appointmentDate = moment(date)
  const theme = useTheme()

  // Priority order for displaying reason for consult:
  // 1. issueTypeId (translated via health-issue-types namespace)
  // 2. reasonForConsult (plain text fallback)
  let displayReason = ''

  if (issueTypeId) {
    // Fallback to nothing if no issue type translation found
    displayReason = I18n.t(`health-issue-types:${issueTypeId}`, '')
  } else if (reasonForConsult) {
    displayReason = reasonForConsult
  }

  return (
    <Container testID="APPT_CARD_STATIC" {...rest}>
      <DateContainer>
        <Typography variant="h1" align="center" color="darkText" testID="APPT_DATE_DAY">
          {appointmentDate.format('D')}
        </Typography>
        <Typography variant="bodyLarge" align="center" color="darkText" testID="APPT_DATE_MONTH">
          {appointmentDate.format('MMM')}
        </Typography>
      </DateContainer>
      <AppointmentDetails>
        <TimeContainer>
          <ScheduleIcon
            size={theme.metrics.icon.small}
            name="schedule"
            color={theme.colors.accent}
          />
          <TimeCopy variant="subheader">{timeslot}</TimeCopy>
        </TimeContainer>
        <ProviderContainer>
          <ProviderImage source={icon ? { uri: icon } : Images.avatarDialogue} />
          <ProviderInnerContainer>
            <Typography variant="h3">{providerName}</Typography>
            {providerRole && <Typography variant="subheader">{providerRole}</Typography>}
          </ProviderInnerContainer>
        </ProviderContainer>
        {displayReason.length > 0 && (
          <ReasonContainer>
            <Typography>
              <Typography variant="subheaderBold">
                {I18n.t('Appointments.reasonForConsult')}
              </Typography>
              <Typography variant="bodyNormal">{displayReason}</Typography>
            </Typography>
          </ReasonContainer>
        )}
      </AppointmentDetails>
    </Container>
  )
}

export default AppointmentCardStatic
